import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

export type TColumnOperationType = 'archiving' | 'restoring' | null;

interface SpaceUpdateWebsocketStore {
  /**
   * Track which column is currently being archived or restored
   * Key: spaceColumnId, Value: operation type
   */
  columnOperations: Record<string, TColumnOperationType>;

  /**
   * Actions
   */
  actions: {
    /**
     * Set a column operation (archiving or restoring)
     */
    setColumnOperation: (spaceColumnId: string, operation: TColumnOperationType) => void;

    /**
     * Clear a column operation
     */
    clearColumnOperation: (spaceColumnId: string) => void;

    /**
     * Clear all column operations
     */
    clearAllColumnOperations: () => void;

    /**
     * Get the operation for a specific column
     */
    getColumnOperation: (spaceColumnId: string) => TColumnOperationType;
  };
}

export const spaceUpdateWebsocketStore = create<SpaceUpdateWebsocketStore>((set, get) => ({
  columnOperations: {},

  actions: {
    /**
     * Set a column operation (archiving or restoring)
     */
    setColumnOperation: (spaceColumnId: string, operation: TColumnOperationType) => {
      if (!spaceColumnId) return;

      set((state) => {
        if (operation === null) {
          // Remove the operation if setting to null
          const { [spaceColumnId]: _, ...rest } = state.columnOperations;
          return { columnOperations: rest };
        }

        return {
          columnOperations: {
            ...state.columnOperations,
            [spaceColumnId]: operation,
          },
        };
      });
    },

    /**
     * Clear a column operation
     */
    clearColumnOperation: (spaceColumnId: string) => {
      if (!spaceColumnId) return;

      set((state) => {
        const { [spaceColumnId]: _, ...rest } = state.columnOperations;
        return { columnOperations: rest };
      });
    },

    /**
     * Clear all column operations
     */
    clearAllColumnOperations: () => {
      set({ columnOperations: {} });
    },

    /**
     * Get the operation for a specific column
     */
    getColumnOperation: (spaceColumnId: string): TColumnOperationType => {
      const state = get();
      return state.columnOperations[spaceColumnId] || null;
    },
  },
}));

// Selectors

/**
 * Get the operation state for a specific column
 */
export const useColumnOperation = (spaceColumnId: string) =>
  spaceUpdateWebsocketStore(useShallow((state) => state.columnOperations[spaceColumnId] || null));

/**
 * Get all column operations
 */
export const useAllColumnOperations = () => spaceUpdateWebsocketStore(useShallow((state) => state.columnOperations));

/**
 * Get actions for managing column operations
 */
export const useSpaceUpdateWebsocketActions = () => spaceUpdateWebsocketStore(useShallow((state) => state.actions));
